import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactModal from 'react-modal';
import { useMediaQuery } from 'react-responsive';

import CloseIcon, { SIZES } from 'components/CloseIcon';
import IconButton from 'components/IconButton';
import { largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

// Webpack sets this variable so process is enabled here
const appElementId = process.env.STORYBOOK === 'true' ? '#storybook-root' : '#main-content';

const Modal = ({ children, className, onRequestClose, overlayOpacity, ...adjustedProps }) => {
  const classes = classNames('Modal__content', className);
  const isLargeUp = useMediaQuery({ query: largeUp });

  ReactModal.setAppElement(appElementId);

  return (
    <ReactModal className={classes} onRequestClose={onRequestClose} {...adjustedProps}>
      <IconButton
        className="Modal__close"
        onClick={onRequestClose}
        aria-label={I18n.localize('app.closeModal')}
      >
        <CloseIcon size={isLargeUp ? SIZES.LARGE : SIZES.MEDIUM} />
      </IconButton>
      {children}
      <style jsx global>{`
        .Modal__overlay {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          background-color: rgba(0, 0, 0, ${overlayOpacity});
        }

        .Modal__content {
          position: relative;
          background-color: ${Theme.get('colors-white')};
          padding: ${Theme.get('spacing') * 7}px ${Theme.get('spacing') * 3}px;
          min-width: calc(95vw - ${Theme.get('spacing') * 6}px);
          max-height: 95%;
          max-width: 95vw;
          margin: ${Theme.get('spacing') * 3}px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          outline: none;
        }

        @media ${largeUp} {
          .Modal__content {
            min-width: auto;
            padding: ${Theme.get('spacing') * 10}px ${Theme.get('spacing') * 24}px;
            max-width: ${Theme.get('container-width-max-px')};
          }
        }

        @keyframes transition-in {
          0% {
            opacity: 0;
            transform: translateY(500px);
          }
          40% {
            opacity: 1;
            transform: translateY(-20px);
          }
          60% {
            transform: translateY(10px);
          }
          100% {
            transform: translateY(0);
          }
        }

        .Modal__content.ReactModal__Content--after-open {
          animation-name: transition-in;
          animation-duration: 650ms;
          animation-direction: normal;
          animation-iteration-count: 1;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
        }

        .Modal__body {
          position: relative;
          background-color: ${Theme.get('colors-white')};
        }

        .Modal__close {
          position: absolute;
          top: ${Theme.get('spacing') * 3 - SIZES.MEDIUM / 2}px;
          right: ${Theme.get('spacing') * 3 - SIZES.MEDIUM / 2}px;
          z-index: 2;
        }

        @media ${largeUp} {
          .Modal__close {
            top: ${Theme.get('spacing') * 6 - SIZES.LARGE / 2}px;
            right: ${Theme.get('spacing') * 6 - SIZES.LARGE / 2}px;
          }
        }

        .Modal--html-open,
        .Modal--body-open {
          overflow: hidden;
        }
      `}</style>
    </ReactModal>
  );
};

Modal.propTypes = {
  portalClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  overlayOpacity: PropTypes.number,
  className: PropTypes.string,
  bodyOpenClassName: PropTypes.string,
  htmlOpenClassName: PropTypes.string,
  closeTimeoutMS: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

Modal.defaultProps = {
  portalClassName: 'Modal__portal',
  overlayClassName: 'Modal__overlay',
  overlayOpacity: 0,
  className: 'Modal__content',
  bodyOpenClassName: 'Modal--body-open',
  htmlOpenClassName: 'Modal--html-open',
  closeTimeoutMS: 0,
};

export default Modal;
